import React, { memo, useCallback } from 'react';
import { injectIntl } from 'react-intl';
import { END_CALL } from 'constants/lsqaConstants';
import { ReactComponent as EndCallIcon } from '@lifesize/ux-assets/vectors/svgIcons/end-call.svg';
import { useDispatch, useSelector } from 'react-redux';
import { openInCallModal } from 'actions/InCallModal';
import Modals from 'constants/Modals';
import { hooks, signaling, selectors, constants } from '@lifesize/clients.sdk';
import { navigateToTrialPage } from 'actions/Application';
import { Fab, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { colors } from '@lifesize/ux-assets';
import { WEB_CONFERENCE_DISABLED } from 'constants/Call';
import useIsMinimized from 'hooks/useIsMinimized';
import useIsKiosk from 'hooks/useIsKiosk';
import lsConfig from 'utils/global-config';
import { featureSelectors } from '@lifesize/nucleus';
import { FEATURE_FLAG_KIOSK_AGENT_ROLE } from 'constants/FeatureFlags';

export interface EndCallButtonProps {
  intl: { formatMessage: Function };
  onEndCallClick?: () => void;
}

export interface StyleProps {
  color: string;
  opacity: number;
}

const EndCallButton = (props: EndCallButtonProps) => {
  const { intl, onEndCallClick } = props;
  const dispatch = useDispatch();
  const conferenceInfo = useSelector(selectors.conferenceInfo);
  const canKickAll = hooks.useCanCurrentUserKickAll();
  const isGuest = hooks.useUserInfo()?.isGuest;
  const isKiosk = useIsKiosk();
  const classes = useStyles({ color: isKiosk ? colors.alertRed : colors.white, opacity: isKiosk ? 1 : 0.6 });
  const isMinimized: boolean = useIsMinimized();
  const isVMR = conferenceInfo.type === constants.GALAXY_CONFERENCE_TYPE_VMR;
  const isAgentRole = useSelector(featureSelectors.getFeatureFlag)(FEATURE_FLAG_KIOSK_AGENT_ROLE);

  const onClick = useCallback(
    async (
      dispatch,
      canKickAll: boolean,
      isGuest: boolean,
      isMinimized: boolean,
      isVMR: boolean,
      isKiosk: boolean,
      onEndCallClick?: Function
    ) => {
      dispatch({ type: WEB_CONFERENCE_DISABLED }); // CA-2581
      if (!!isAgentRole && !isKiosk && !isGuest) {
        dispatch(openInCallModal(Modals.endCallForAll));
        return;
      }
      if (!!onEndCallClick) {
        onEndCallClick();
        return;
      }
      if (isKiosk) {
        lsConfig.skipTimeOnBeforeUnload();
        await signaling.endCall();
        window.open('about:blank', '_self');
        window.close();
        return;
      }
      if (isGuest) {
        await signaling.endCall();
        dispatch(navigateToTrialPage());
        return;
      }
      if (canKickAll) {
        dispatch(openInCallModal(Modals.endCallForAll));
      } else {
        await signaling.endCall();
      }
    },
    [isAgentRole]
  );

  return (
    <Tooltip
      title={intl.formatMessage({ id: 'components.incall.controls.endCall.tooltip', defaultMessage: 'Leave' })}
      placement="top"
      arrow={true}
    >
      <span data-testid="end-call-btn">
        <Fab
          classes={classes}
          disableFocusRipple={true}
          onClick={() => onClick(dispatch, canKickAll, isGuest, isMinimized, isVMR, isKiosk, onEndCallClick)}
          size="medium"
          {...END_CALL}
        >
          <EndCallIcon data-testid="end-call-icon" />
        </Fab>
      </span>
    </Tooltip>
  );
};

const useStyles = makeStyles<Theme, StyleProps>(() => ({
  root: {
    backgroundColor: 'transparent',
    border: (props) => `2px solid ${props.color}`,
    boxShadow: 'none',
    color: (props) => props.color,
    margin: 0,
    opacity: (props) => props.opacity,
    zIndex: 1050, // enable end call button in guest flow.
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: (props) => props.color,
      color: (props) => props.color,
      opacity: 1
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  }
}));

export default memo(injectIntl(EndCallButton));
